import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { KPI } from '../types';
import { computeKPIProgress, computeKPIHealth, formatValue, healthColors } from '../helpers';
import { useOKRPermissions, isFinancialKPI } from '../useOKRPermissions';
import { TrendingUp, TrendingDown, Minus, RefreshCw, EyeOff } from 'lucide-react';

// ── SVG semi-circle gauge ─────────────────────────────────────────────────────

interface GaugeProps { progress: number; health: string; size?: number }

const KPIGauge: React.FC<GaugeProps> = ({ progress, health, size = 96 }) => {
    const r = size * 0.38;
    const cx = size / 2;
    const cy = size * 0.58;
    const sw = size * 0.075;
    const color = (healthColors as Record<string, string>)[health] ?? '#A3AED0';
    const pct = Math.min(100, Math.max(0, progress));

    const bgArc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
    let progressArc = '';
    if (pct > 0) {
        const angle = (1 - pct / 100) * Math.PI;
        const ex = cx + r * Math.cos(angle);
        const ey = cy - r * Math.sin(angle);
        progressArc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${ex} ${ey}`;
    }
    return (
        <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`}>
            <path d={bgArc} fill="none" stroke="#E0E5F2" strokeWidth={sw} strokeLinecap="round" />
            {pct > 0 && <path d={progressArc} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" />}
            <text x={cx} y={cy - 4} textAnchor="middle" dominantBaseline="auto"
                fontSize={size * 0.19} fontWeight="700" fill={color} fontFamily="system-ui,sans-serif">
                {Math.round(pct)}%
            </text>
        </svg>
    );
};

// ── Sparkline chart ───────────────────────────────────────────────────────────

const Sparkline: React.FC<{ data: { date: string; value: number }[]; color: string }> = ({ data, color }) => {
    const options = useMemo(() => ({
        chart: { type: 'line' as const, sparkline: { enabled: true }, animations: { enabled: false } },
        stroke: { curve: 'smooth' as const, width: 2 },
        colors: [color],
        tooltip: {
            fixed: { enabled: false },
            x: { show: true, formatter: (_: any, opts: any) => data[opts.dataPointIndex]?.date ?? '' },
            y: { formatter: (v: number) => v.toLocaleString('fr-FR') },
            marker: { show: false },
        },
    }), [color, data]);
    const series = useMemo(() => [{ data: data.map((d) => d.value) }], [data]);
    return <ReactApexChart options={options} series={series} type="line" width="100%" height={52} />;
};

// ── KPI card ─────────────────────────────────────────────────────────────────

const KPICard: React.FC<{ kpi: KPI }> = ({ kpi }) => {
    const { canSeeFinancials } = useOKRPermissions();
    const financial = isFinancialKPI(kpi.unit, kpi.metric_key);
    const masked = financial && !canSeeFinancials;

    const progress = computeKPIProgress(kpi);
    const health = computeKPIHealth(kpi);
    const color = (healthColors as Record<string, string>)[health];

    const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
    const trendGood = (kpi.direction === 'higher-is-better' && kpi.trend === 'up') || (kpi.direction === 'lower-is-better' && kpi.trend === 'down');
    const trendCls = trendGood ? 'text-green-500' : kpi.trend === 'stable' ? 'text-gray-400' : 'text-red-500';

    const borderBg: Record<string, string> = { good: 'border-green-100 bg-green-50/30', warning: 'border-orange-100 bg-orange-50/30', critical: 'border-red-100 bg-red-50/30' };

    const maskedValue = '••••';
    const showVal = (v: number) => (masked ? maskedValue : formatValue(v, kpi.unit));

    return (
        <div className={`rounded-2xl border p-4 flex flex-col gap-2 ${borderBg[health]}`}>
            <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-navy-700 leading-snug">{kpi.name}</p>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {masked && <span title="Valeur confidentielle"><EyeOff size={12} className="text-gray-300" /></span>}
                    {kpi.auto_sync && <span title="Synchronisation automatique"><RefreshCw size={11} className="text-toola-400" /></span>}
                    <TrendIcon size={15} className={trendCls} />
                </div>
            </div>

            <div className="flex items-center gap-4">
                <KPIGauge progress={progress} health={health} size={80} />
                <div className="flex-1 min-w-0">
                    <p className={`text-xl font-bold ${masked ? 'blur-sm select-none' : ''}`} style={{ color }}>
                        {showVal(kpi.currentValue)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Cible : <span className={`font-medium text-navy-700 ${masked ? 'blur-sm select-none' : ''}`}>{showVal(kpi.targetValue)}</span>
                    </p>
                    <p className={`text-xs text-gray-400 mt-0.5 ${masked ? 'blur-sm select-none' : ''}`}>
                        Min : {showVal(kpi.minAcceptableValue)}
                    </p>
                    {kpi.projection != null && !masked && (
                        <p className="text-xs mt-0.5" style={{ color }}>
                            Proj. fin : <span className="font-semibold">{formatValue(kpi.projection, kpi.unit)}</span>
                        </p>
                    )}
                </div>
            </div>

            {kpi.history.length > 1 && (
                <div className="-mx-1">
                    <Sparkline data={kpi.history} color={color} />
                </div>
            )}

            <div className="flex items-center justify-between text-xs text-gray-400 pt-1 border-t border-gray-100">
                <span>{kpi.direction === 'higher-is-better' ? '↑ Plus haut = mieux' : '↓ Plus bas = mieux'}</span>
                <span className="font-medium" style={{ color }}>{Math.round(progress)}% atteint</span>
            </div>
        </div>
    );
};

export default KPICard;
